import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import {
    AppScreen, AppBar, Section, SectionHeader, Badge, Button, ButtonFooter, Chip,
    TextField, TextArea, FieldGroup, SwitchList, SwitchRow, ActionRow, ActionList, toDateKey,
} from 'components/ds';
import { useMeetingDetail } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { PlaceMapView, MapPlace } from 'components/map';
import { isInKorea } from 'common/utils/regionDetector';
import { isNativeApp, openNativePlaceExplore } from 'common/webview';
import config from 'config/config';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { useUserLocation } from 'hooks/useUserLocation';
import { findKnownDestinationCenter } from 'common/api/tourApi';
import { SubPlaceModal, SubPlace, SubPlaceForm } from 'components/meeting/SubPlaceModal';
import { MeetingRepository } from 'repository/meetingRepository';
import styles from './MeetingEdit.module.css';
import { toLocalISOString, toHHmm, toKoreanTime } from 'common/utils/datetime';


// 인라인 SVG — 이모지 금지 (UI 심볼은 모두 SVG 규칙).

export const MeetingEdit = () => {
    const navigate = useNavigate();
    const routeLocation = useLocation();
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useAlert();
    const { meetingId } = useParams();
    const [searchParams] = useSearchParams();
    const { data: meetingData } = useMeetingDetail();
    // 푸시 탭으로 들어올 때 ?focus=place 면 상세 로드 후 자동으로 장소 검색으로 이동
    const focusParam = searchParams.get('focus');
    const autoFocusConsumedRef = useRef(false);

    // Pre-filled data from existing meeting
    const [meetingName, setMeetingName] = useState('');
    const [description, setDescription] = useState('');
    const [isRegularMeeting, setIsRegularMeeting] = useState(false);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [location, setLocation] = useState('');
    const [category, setCategory] = useState('');
    const [maxMembers, setMaxMembers] = useState(10);
    const [isPublic, setIsPublic] = useState(true);
    const [repeatType, setRepeatType] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [repeatInterval, setRepeatInterval] = useState(1);
    const [repeatDaysOfWeek, setRepeatDaysOfWeek] = useState<string[]>([]);
    const [repeatDayOfMonth, setRepeatDayOfMonth] = useState(1);
    const [repeatTime, setRepeatTime] = useState('19:00');

    // 여행 모임 관련 상태
    const [meetingType, setMeetingType] = useState<'DAY' | 'TRAVEL'>('DAY');
    const [travelDestination, setTravelDestination] = useState('');
    const [travelType, setTravelType] = useState<'DOMESTIC' | 'OVERSEAS'>('DOMESTIC');
    const [travelStartDate, setTravelStartDate] = useState<Date | null>(null);
    const [travelEndDate, setTravelEndDate] = useState<Date | null>(null);
    const [meetingTime, setMeetingTime] = useState('');
    // 숙박지는 sub_place(category='HOTEL') 행으로 통일 — 단일 hotelName/Address state 제거.
    // MeetingEdit 진입 시 subPlaces fetch 후 HOTEL 카테고리만 필터링해 카드 리스트 표시.
    const [hotelSubPlaces, setHotelSubPlaces] = useState<SubPlace[]>([]);
    // 일반 sub_place (HOTEL 제외) — 모임 수정에서 추가/수정/삭제 가능하도록 카드 리스트로 노출
    const [generalSubPlaces, setGeneralSubPlaces] = useState<SubPlace[]>([]);
    const [showSubPlaceModal, setShowSubPlaceModal] = useState(false);
    const [editingSubPlace, setEditingSubPlace] = useState<SubPlace | null>(null);
    const [subPlaceFormPrefill, setSubPlaceFormPrefill] = useState<SubPlaceForm | undefined>(undefined);
    const [locationStateProcessed, setLocationStateProcessed] = useState(false);

    // 숙박지는 SubPlaceModal 의 지도 검색 흐름으로 다룹니다.
    // 저장 payload 에 남아 있는 legacy hotelName/hotelAddress 만 유지합니다.
    const [hotelName, setHotelName] = useState('');
    const [hotelAddress, setHotelAddress] = useState('');

    // 집결지 지도 모달 — 여행 모임 전용 (일반 모임은 PlaceSearch navigate 유지)
    const [showGatherMap, setShowGatherMap] = useState(false);
    const [gatherPlaces, setGatherPlaces] = useState<MapPlace[]>([]);
    // 네이티브 탐색 화면을 두 번 열지 않기 위한 표시
    const nativeGatherOpenedRef = useRef(false);
    const [selectedGatherDetail, setSelectedGatherDetail] = useState<MapPlace | null>(null);
    const [gatherAnchorCenter, setGatherAnchorCenter] = useState<{ lat: number; lng: number } | null>(null);
    const [locationAddress, setLocationAddress] = useState('');
    /* PlaceSearch 에서 새로 고른 장소는 아직 서버에 없습니다.
       모임 상세 응답이 뒤늦게 도착하면 옛 장소로 되돌아가 버려서, 한 번 고른
       뒤로는 초기화 effect 가 장소 칸을 건드리지 않게 표시해 둡니다. */
    const placePickedRef = useRef(false);
    const [locationLat, setLocationLat] = useState<number | null>(null);
    const [locationLng, setLocationLng] = useState<number | null>(null);

    // 사용자 GPS — 기존 집결지가 없을 때 지도 첫 화면 (PlaceSearch 와 동일 흐름)
    const { location: userGPS, fetchLocation: fetchUserGPS } = useUserLocation();
    useEffect(() => {
        // 모달 진입 시점에 GPS 한 번 확보 (cache hit 또는 새 측위)
        if (showGatherMap) {
            fetchUserGPS();
        }
    }, [showGatherMap]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
        }
    }, [meetingId]);

    // 숙박지 sub_place 목록 fetch — HOTEL 카테고리만
    const fetchHotelSubPlaces = useCallback(async () => {
        if (!meetingId) return;
        try {
            const res: any = await MeetingRepository.onGetSubPlaces(meetingId, { category: 'HOTEL' });
            const list = res?.data?.data || res?.data || [];
            if (Array.isArray(list)) {
                // 체크인 일자 오름차순으로 정렬
                const sorted = [...list].sort((a, b) => {
                    const ta = a.startDatetime ? new Date(a.startDatetime).getTime() : 0;
                    const tb = b.startDatetime ? new Date(b.startDatetime).getTime() : 0;
                    return ta - tb;
                });
                setHotelSubPlaces(sorted);
            }
        } catch (e) {
            console.warn('[MeetingEdit] 숙박지 조회 실패:', e);
        }
    }, [meetingId]);

    // 일반 sub_place 목록 fetch — HOTEL 제외 전체
    const fetchGeneralSubPlaces = useCallback(async () => {
        if (!meetingId) return;
        try {
            const res: any = await MeetingRepository.onGetSubPlaces(meetingId);
            const list = res?.data?.data || res?.data || [];
            if (Array.isArray(list)) {
                const filtered = list.filter((sp: SubPlace) => sp.category !== 'HOTEL');
                const sorted = [...filtered].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                setGeneralSubPlaces(sorted);
            }
        } catch (e) {
            console.warn('[MeetingEdit] 일반 장소 조회 실패:', e);
        }
    }, [meetingId]);

    useEffect(() => {
        fetchHotelSubPlaces();
        fetchGeneralSubPlaces();
    }, [fetchHotelSubPlaces, fetchGeneralSubPlaces]);

    // PlaceSearch / TimePicker 에서 SubPlaceModal 로 복귀 — 모달 다시 열고 폼 복원
    // (없으면 사용자가 지도에서 장소 선택 후 돌아왔을 때 호텔이 저장 안 되는 것처럼 보임)
    // editingSubPlaceId 가 있는데 sub_place 아직 fetched 전이면 wait — 안 그러면 update 가 create 로 잘못 처리됨
    useEffect(() => {
        if (!routeLocation.state?.fromSubPlaceModal || locationStateProcessed) return;
        const editingId = (routeLocation.state as any).editingSubPlaceId;
        if (editingId && (hotelSubPlaces.length === 0 && generalSubPlaces.length === 0)) return;

        const savedForm = (routeLocation.state as any).subPlaceForm as SubPlaceForm | undefined;
        const placeInfo = (routeLocation.state as any).placeInfo as { name?: string; address?: string } | undefined;
        const selectedTime = (routeLocation.state as any).selectedTime as string | undefined;
        const editingSubPlaceId = (routeLocation.state as any).editingSubPlaceId as number | undefined;

        const fallbackHotelForm: SubPlaceForm = {
            placeName: '', placeAddress: '', category: 'HOTEL',
            dayNumber: 1, dayNumbers: [1], visitTime: '', description: '',
            checkInDate: '', checkInTime: '15:00', checkOutDate: '', checkOutTime: '11:00',
        };

        if (selectedTime && savedForm) {
            setSubPlaceFormPrefill({ ...savedForm, visitTime: selectedTime });
        } else if (placeInfo) {
            // 좌표(lat/lng) 도 form 에 함께 저장 — 누락되면 sub_place 가 좌표 없이 등록되어 미니지도 표시 깨짐
            const piAny = placeInfo as any;
            const latNum = typeof piAny.lat === 'number' ? piAny.lat : (piAny.lat ? parseFloat(piAny.lat) : undefined);
            const lngNum = typeof piAny.lng === 'number' ? piAny.lng : (piAny.lng ? parseFloat(piAny.lng) : undefined);
            setSubPlaceFormPrefill({
                ...(savedForm || fallbackHotelForm),
                placeName: placeInfo.name || savedForm?.placeName || '',
                placeAddress: placeInfo.address || savedForm?.placeAddress || '',
                ...(typeof latNum === 'number' && !isNaN(latNum) ? { latitude: latNum } : {}),
                ...(typeof lngNum === 'number' && !isNaN(lngNum) ? { longitude: lngNum } : {}),
                ...(piAny.externalPlaceId || piAny.placeId ? { placeId: piAny.externalPlaceId || piAny.placeId } : {}),
            });
        } else if (savedForm) {
            setSubPlaceFormPrefill(savedForm);
        }

        if (editingSubPlaceId) {
            const all = [...hotelSubPlaces, ...generalSubPlaces];
            const editingPlace = all.find(sp => sp.subPlaceId === editingSubPlaceId);
            if (editingPlace) setEditingSubPlace(editingPlace);
        }

        setShowSubPlaceModal(true);
        setLocationStateProcessed(true);
    }, [routeLocation.state, hotelSubPlaces, generalSubPlaces, locationStateProcessed]);

    // PlaceSearch 에서 대표 장소를 고르고 돌아왔을 때 — 폼에 반영.
    // 서브 장소(fromSubPlaceModal) 복귀는 위 effect 가 따로 처리하므로 여기서는 제외합니다.
    // 이 처리가 없으면 장소를 골라도 화면이 그대로라 "수정이 안 된다" 로 보입니다.
    useEffect(() => {
        const st = routeLocation.state as any;
        if (!st?.placeInfo || st.fromSubPlaceModal) return;
        const pi = st.placeInfo;
        placePickedRef.current = true;
        if (pi.name) setLocation(pi.name);
        if (pi.address) setLocationAddress(pi.address);
        const lat = typeof pi.lat === 'number' ? pi.lat : (pi.lat ? parseFloat(pi.lat) : null);
        const lng = typeof pi.lng === 'number' ? pi.lng : (pi.lng ? parseFloat(pi.lng) : null);
        if (lat != null && !isNaN(lat)) setLocationLat(lat);
        if (lng != null && !isNaN(lng)) setLocationLng(lng);
        // 한 번 반영한 뒤 지웁니다 — 남겨 두면 뒤로 갔다 오면서 다시 덮어씁니다
        navigate(routeLocation.pathname, { replace: true, state: {} });
    }, [routeLocation.key]); // eslint-disable-line react-hooks/exhaustive-deps

    /* 모임 상세의 '집결지' 버튼으로 들어온 경우 — 수정 폼을 지나쳐 지도를 바로 엽니다.
       여기까지 온 사람은 집결지를 정하려고 온 것이고, 폼에서 그 칸을 다시 찾게 하면
       상세에 집결지 입구가 없던 때와 크게 다르지 않습니다. 한 번만 열고 state 는 비웁니다. */
    useEffect(() => {
        const st = routeLocation.state as any;
        if (!st?.openGather) return;
        setShowGatherMap(true);
        navigate(routeLocation.pathname, { replace: true, state: {} });
    }, [routeLocation.key]); // eslint-disable-line react-hooks/exhaustive-deps

    // 여행지 변경 후 TravelDestination 에서 돌아왔을 때 sessionStorage 의 새 destination 읽어 갱신.
    // location.key 를 deps 에 포함 — navigate(replace) 로 같은 path 로 돌아와도 key 가 바뀌어 재실행됨.
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('meeting_create_data');
            if (!raw) return;
            const data = JSON.parse(raw);
            if (!data?.isEditMode || data.meetingId !== meetingId) return;
            if (data.destination) setTravelDestination(data.destination);
            if (data.travelType) setTravelType(data.travelType);
            sessionStorage.removeItem('meeting_create_data');
        } catch { /* ignore */ }
    }, [meetingId, routeLocation.key]);

    // 여행지 좌표 — 응답의 latitude/longitude 는 집결지일 수 있어
    // travelDestination 문자열을 geocode 해 확보. backend search 우선, fail 시 Nominatim 직접.
    const [destResolvedCenter, setDestResolvedCenter] = useState<{ lat: number; lng: number } | null>(null);
    useEffect(() => {
        const dest = (travelDestination || '').trim();
        if (!dest) { setDestResolvedCenter(null); return; }
        let cancelled = false;

        // 1순위 — 로컬 매핑 (KR_POPULAR_CITIES / JP_POPULAR_SPOTS).
        // 강릉=경포해변, 부산=해운대 등 휴양지 중심 좌표 사용 → 추천 호텔/관광이 실제 휴양지 부근에서 검색됨.
        const known = findKnownDestinationCenter(dest);
        if (known) {
            setDestResolvedCenter(known);
            return;
        }

        const fetchNominatim = async () => {
            try {
                const r = await fetch(
                    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(dest)}&format=json&limit=1&accept-language=ko,en,ja`,
                    { headers: { 'User-Agent': 'DOKOCREW-App/1.0' } },
                );
                const data = await r.json();
                if (cancelled) return;
                if (data?.[0]?.lat && data?.[0]?.lon) {
                    setDestResolvedCenter({ lat: Number(data[0].lat), lng: Number(data[0].lon) });
                }
            } catch { /* 무시 — 다음 단계 fallback */ }
        };

        instance.get(getApiUrl('travelSearch'), {
            params: { q: dest, acceptLanguage: 'ko,en,ja' },
        }).then(async res => {
            if (cancelled) return;
            const top = (res?.data?.data || [])[0];
            if (top?.lat && top?.lng) {
                setDestResolvedCenter({ lat: Number(top.lat), lng: Number(top.lng) });
            } else {
                // 백엔드 응답에 결과 없음 → Nominatim 직접 fallback
                await fetchNominatim();
            }
        }).catch(() => {
            // 백엔드 호출 자체가 실패 (미배포/네트워크) → Nominatim 직접 fallback
            fetchNominatim();
        });
        return () => { cancelled = true; };
    }, [travelDestination]);

    const destCenter = useMemo(
        () => destResolvedCenter
            || (meetingData?.data?.latitude && meetingData?.data?.longitude
                ? { lat: meetingData.data.latitude, lng: meetingData.data.longitude }
                : undefined),
        [destResolvedCenter, meetingData?.data?.latitude, meetingData?.data?.longitude],
    );

    /* 집결지 지도 center — selectedDetail (클릭) > anchor (지도 이동) > 기존 집결지 > 사용자 GPS
       여행지(destCenter) 는 일부러 넣지 않습니다. 집결지는 떠나는 곳이라 파리 여행이라도
       인천공항이어야 하는데, 여행지를 넣으면 지도가 파리에서 열려 한국 집결지를
       고를 수 없었습니다. 생성 흐름(TravelGatherPlace)도 같은 순서입니다. */
    const gatherMapCenter = useMemo(
        () => {
            if (selectedGatherDetail) return { lat: selectedGatherDetail.lat, lng: selectedGatherDetail.lng };
            if (gatherAnchorCenter) return gatherAnchorCenter;
            if (locationLat && locationLng) return { lat: locationLat, lng: locationLng };
            if (userGPS?.lat && userGPS?.lng) return { lat: userGPS.lat, lng: userGPS.lng };
            return undefined;
        },
        [selectedGatherDetail?.lat, selectedGatherDetail?.lng, gatherAnchorCenter, locationLat, locationLng, userGPS?.lat, userGPS?.lng],
    );

    // 집결지 추천 — 지도가 보고 있는 곳(=출발지) 근처 음식점/카페/관광. 여행지 근처가 아닙니다.
    useEffect(() => {
        if (!showGatherMap) return;
        const center = gatherAnchorCenter
            || (locationLat && locationLng ? { lat: locationLat, lng: locationLng } : null)
            || (userGPS?.lat && userGPS?.lng ? { lat: userGPS.lat, lng: userGPS.lng } : null);
        if (!center) return;
        let cancelled = false;
        const fetchGather = async () => {
            const types = ['restaurant', 'cafe', 'attraction'];
            const results = await Promise.allSettled(
                types.map(type =>
                    instance.get(getApiUrl('nearbyPlaces'), {
                        params: { lat: center.lat, lng: center.lng, type, radius: 3000, limit: 8 }
                    }).then(res => ({ type, data: res?.data?.data || [] }))
                )
            );
            if (cancelled) return;
            const places: MapPlace[] = [];
            const addedNames = new Set<string>();
            for (const result of results) {
                if (result.status !== 'fulfilled') continue;
                const { type, data } = result.value;
                for (const p of data) {
                    if (addedNames.has(p.name)) continue;
                    addedNames.add(p.name);
                    places.push({
                        id: `${p.placeId || p.name}-${type}`,
                        name: p.name,
                        address: p.address || '',
                        lat: p.lat,
                        lng: p.lng,
                        imageUrl: p.photoUrl || undefined,
                        categoryIcon: type === 'restaurant' ? 'restaurant' : type === 'cafe' ? 'cafe' : 'attraction',
                        categoryLabel: p.categoryLabel || (type === 'restaurant' ? t('meetingForm.restaurantCat') : type === 'cafe' ? t('meetingForm.cafeCat') : t('meetingForm.attractionCat')),
                    });
                }
            }

            /* 해외 + 네이티브 → 네이티브 탐색 화면으로 넘깁니다 (한 번만).
               웹뷰 안에서 해외 지도를 직접 그리지 않기 위한 것입니다 —
               다른 장소 고르기 화면(PlaceSearch·PlaceExplore 등)도 모두 같은 방식입니다. */
            if (isNativeApp() && !isInKorea(center.lat, center.lng) && !nativeGatherOpenedRef.current) {
                nativeGatherOpenedRef.current = true;
                const result = await openNativePlaceExplore({
                    places: places.filter(p => p.lat && p.lng).map(p => ({
                        id: p.id, name: p.name, address: p.address || '', lat: p.lat!, lng: p.lng!,
                        imageUrl: p.imageUrl, rating: p.rating, categoryLabel: p.categoryLabel,
                    })),
                    center,
                    confirmButtonText: t('meetingForm.selectThisLocation'),
                    title: t('meetingForm.gatherPlace'),
                    apiUrl: config.API_URL,
                    mode: 'general',
                });
                setShowGatherMap(false);
                setSelectedGatherDetail(null);
                nativeGatherOpenedRef.current = false;
                if (result) {
                    placePickedRef.current = true;
                    setLocation(result.name);
                    setLocationAddress(result.address || '');
                    setLocationLat(result.lat);
                    setLocationLng(result.lng);
                }
                return;
            }

            setGatherPlaces(places);
        };
        fetchGather();
        return () => { cancelled = true; };
    }, [showGatherMap, gatherAnchorCenter?.lat, gatherAnchorCenter?.lng, locationLat, locationLng, userGPS?.lat, userGPS?.lng]); // eslint-disable-line react-hooks/exhaustive-deps


    // API 데이터로 폼 초기화
    useEffect(() => {
        if (meetingData?.data) {
            const meeting = meetingData.data;

            setMeetingName(meeting.meetingName || '');
            setDescription(meeting.description || '');
            setCategory(meeting.category || '');
            setMaxMembers(meeting.maxMembers || 10);
            setIsPublic(meeting.isPublic !== false);
            setIsRegularMeeting(meeting.isRegularMeeting || false);
            setRepeatType(meeting.repeatType || 'WEEKLY');
            setRepeatInterval(meeting.repeatInterval || 1);
            setRepeatDaysOfWeek(
                meeting.repeatDaysOfWeek
                    ? String(meeting.repeatDaysOfWeek).split(',').filter(Boolean)
                    : []
            );
            setRepeatDayOfMonth(meeting.repeatDayOfMonth || 1);
            setRepeatTime(meeting.repeatTime || '19:00');
            if (!placePickedRef.current) {
                setLocation(meeting.placeName || '');
                setLocationAddress(meeting.placeAddress || '');
                setLocationLat(meeting.latitude != null ? Number(meeting.latitude) : null);
                setLocationLng(meeting.longitude != null ? Number(meeting.longitude) : null);
            }

            setMeetingType(meeting.meetingType || 'DAY');
            setTravelDestination(meeting.travelDestination || '');
            setTravelType(meeting.travelType || 'DOMESTIC');
            setTravelStartDate(meeting.travelStartDate ? new Date(meeting.travelStartDate) : null);
            setTravelEndDate(meeting.travelEndDate ? new Date(meeting.travelEndDate) : null);
            setMeetingTime(meeting.meetingTime || '');
            setHotelName(meeting.hotelName || '');
            setHotelAddress(meeting.hotelAddress || '');

            if (meeting.schedule && meeting.schedule.startDatetime) {
                try {
                    const startDate = new Date(meeting.schedule.startDatetime);
                    if (!isNaN(startDate.getTime())) {
                        const formattedDate = startDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
                        const formattedTime = startDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
                        setDate(formattedDate);
                        setTime(formattedTime);
                    }
                } catch (error) {
                    console.error('날짜/시간 파싱 오류:', error);
                }
            }
        }
    }, [meetingData]);

    // 푸시 탭 딥링크 — ?focus=place 면 상세 로드 후 1회 자동으로 장소 검색 진입
    useEffect(() => {
        if (focusParam !== 'place') return;
        if (!meetingData?.data) return;
        if (autoFocusConsumedRef.current) return;
        autoFocusConsumedRef.current = true;
        const t2 = setTimeout(() => {
            handleLocationChange();
        }, 50);
        return () => clearTimeout(t2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusParam, meetingData]);

    const isSaveEnabled = meetingName.trim().length > 0;
    const isTravelMeeting = meetingType === 'TRAVEL';

    const travelPeriod = useMemo(() => {
        if (!travelStartDate || !travelEndDate) return null;
        const diffTime = Math.abs(travelEndDate.getTime() - travelStartDate.getTime());
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const nights = days - 1;
        return { days, nights };
    }, [travelStartDate, travelEndDate]);

    const handleSave = async () => {
        if (isSaveEnabled && meetingId) {
            const updateData: any = {
                meetingName,
                description,
                category,
                maxMembers,
                isPublic,
                placeName: location,
                // 이름만 보내면 주소·좌표가 옛 장소 그대로 남아 지도가 엉뚱한 곳을 가리킵니다
                placeAddress: locationAddress || undefined,
                latitude: locationLat ?? undefined,
                longitude: locationLng ?? undefined,
            };

            if (isTravelMeeting) {
                updateData.meetingType = 'TRAVEL';
                updateData.travelDestination = travelDestination;
                updateData.travelType = travelType;
                updateData.travelStartDate = travelStartDate ? toLocalISOString(travelStartDate) : undefined;
                updateData.travelEndDate = travelEndDate ? toLocalISOString(travelEndDate) : undefined;
                updateData.meetingTime = meetingTime;
                // 숙박지는 sub_place 흐름으로 관리 — handleSave PUT 에서는 제외
                // (SubPlaceModal 의 onSaveSuccess 가 즉시 SubPlace API 호출)
            } else {
                updateData.meetingType = 'DAY';
                updateData.isRegularMeeting = isRegularMeeting;
                updateData.repeatType = isRegularMeeting ? repeatType : null;
                updateData.repeatInterval = isRegularMeeting
                    ? (repeatType === 'WEEKLY' ? repeatInterval : 1) : null;
                updateData.repeatDaysOfWeek = isRegularMeeting && repeatType === 'WEEKLY'
                    ? (repeatDaysOfWeek.length > 0 ? repeatDaysOfWeek.join(',') : null) : null;
                updateData.repeatDayOfMonth = isRegularMeeting && repeatType === 'MONTHLY'
                    ? repeatDayOfMonth : null;
                updateData.repeatTime = isRegularMeeting ? repeatTime : null;
            }

            try {
                await meetingStore.onUpdateMeeting(meetingId, updateData);
                navigate(`/meeting/detail/${meetingId}`, { replace: true });
            } catch (error) {
                console.error('모임 수정 실패:', error);
            }
        }
    };

    // Step2 는 sessionStorage('meeting_create_data') 기반으로 모든 필드를 복구한다.
    // location.state 만으로는 meetingName/description/정기설정/여행정보 등이 빈값으로 리셋되므로,
    // 수정 진입 직전에 현재 폼 상태를 sessionStorage 에 명시적으로 기록해서 일관된 상태로 넘긴다.
    const persistEditSnapshotToSession = () => {
        try {
            const snapshot: any = {
                meetingName,
                description,
                category,
                maxMembers,
                isPublic,
                meetingType,
                // 당일 모임 데이터
                selectedPlace: location,
                selectedPlaceInfo: meetingData?.data ? {
                    name: meetingData.data.placeName,
                    address: meetingData.data.placeAddress,
                    lat: meetingData.data.latitude,
                    lng: meetingData.data.longitude,
                } : null,
                selectedDate: meetingData?.data?.schedule?.startDatetime
                    ? new Date(meetingData.data.schedule.startDatetime).toISOString()
                    : undefined,
                selectedTime: time,
                // 정기 모임 설정 복구
                isRegularMeeting,
                repeatType: isRegularMeeting ? repeatType : null,
                repeatInterval: isRegularMeeting
                    ? (repeatType === 'WEEKLY' ? repeatInterval : 1) : null,
                repeatDaysOfWeek: isRegularMeeting && repeatType === 'WEEKLY'
                    ? (repeatDaysOfWeek.length > 0 ? repeatDaysOfWeek.join(',') : null) : null,
                repeatDayOfMonth: isRegularMeeting && repeatType === 'MONTHLY'
                    ? repeatDayOfMonth : null,
                repeatTime: isRegularMeeting ? repeatTime : null,
                // 여행 모임 데이터
                travelDestination,
                travelType,
                travelStartDate: travelStartDate ? toLocalISOString(travelStartDate) : undefined,
                travelEndDate: travelEndDate ? toLocalISOString(travelEndDate) : undefined,
                hotelName,
                hotelAddress,
            };
            sessionStorage.setItem('meeting_create_data', JSON.stringify(snapshot));
        } catch (e) {
            console.warn('[MeetingEdit] persist snapshot 실패:', e);
        }
    };

    const handleDateChange = () => {
        persistEditSnapshotToSession();
        const scheduleDate = meetingData?.data?.schedule?.startDatetime
            ? new Date(meetingData.data.schedule.startDatetime).toISOString()
            : null;
        navigate('/meeting/create/step2', {
            state: {
                selectedPlace: location,
                placeInfo: meetingData?.data ? {
                    name: meetingData.data.placeName,
                    address: meetingData.data.placeAddress,
                    lat: meetingData.data.latitude,
                    lng: meetingData.data.longitude
                } : null,
                selectedTime: time,
                selectedDate: scheduleDate,
                isEditMode: true,
                meetingId: meetingId,
                confirmedScheduleId: meetingData?.data?.confirmedScheduleId,
                meetingName,
                description,
                category,
                maxMembers,
                isPublic,
            }
        });
    };

    const handleLocationChange = () => {
        // 여행 모임은 자체 모달 — 여행지 근처 추천 (PlaceSearch 의 GPS 기반 추천 부적합)
        if (isTravelMeeting) {
            setShowGatherMap(true);
            return;
        }
        persistEditSnapshotToSession();
        const scheduleDate = meetingData?.data?.schedule?.startDatetime
            ? new Date(meetingData.data.schedule.startDatetime).toISOString()
            : null;
        navigate('/meeting/place-search', {
            state: {
                // 이게 없으면 PlaceSearch 가 기본값(/meeting/create/step3) 으로 돌아가
                // 장소를 고르자마자 모임 만들기 3단계 화면이 떠 버립니다
                returnTo: `/meeting/edit/${meetingId}`,
                selectedPlace: location,
                placeInfo: meetingData?.data ? {
                    name: meetingData.data.placeName,
                    address: meetingData.data.placeAddress,
                    lat: meetingData.data.latitude,
                    lng: meetingData.data.longitude
                } : null,
                selectedTime: time,
                selectedDate: scheduleDate,
                isEditMode: true,
                meetingId: meetingId,
                confirmedScheduleId: meetingData?.data?.confirmedScheduleId,
                meetingName,
                description,
                category,
                maxMembers,
                isPublic,
            }
        });
    };

    const wdShort = t('common.weekDaysShort', { returnObjects: true }) as string[];
    const DOW_LABELS = wdShort;

    /* 숙소·추가 장소 삭제 — 두 군데서 같은 흐름이라 하나로 모았습니다 */
    const deleteSubPlace = async (sp: SubPlace, reload: () => void) => {
        if (!meetingId) return;
        const ok = await showConfirm({
            message: t('meetingEdit.deleteConfirm', { name: sp.placeName }),
            confirmText: t('meetingEdit.delete'),
        });
        if (!ok) return;
        try {
            await MeetingRepository.onDeleteSubPlace(meetingId, sp.subPlaceId);
            reload();
        } catch (e) {
            console.error('[MeetingEdit] 장소 삭제 실패:', e);
            showAlert({ message: t('meetingEdit.deleteFail') });
        }
    };

    const dowList: { v: string; label: string }[] = [
        { v: 'SUN', label: wdShort[0] }, { v: 'MON', label: wdShort[1] }, { v: 'TUE', label: wdShort[2] },
        { v: 'WED', label: wdShort[3] }, { v: 'THU', label: wdShort[4] }, { v: 'FRI', label: wdShort[5] },
        { v: 'SAT', label: wdShort[6] },
    ];

    /* 장소 줄 하나 (숙소 · 추가 장소) — 세 군데서 같은 모양이라 하나로 모았습니다 */
    const renderPlaceRow = (
        sp: SubPlace,
        opts: { tag: string; meta?: string; onDelete: () => void },
    ) => (
        <li key={sp.subPlaceId} className={styles.row}>
            <span className={styles.rowBody}>
                <span className={styles.rowTags}>
                    <Badge tone="outline">{opts.tag}</Badge>
                </span>
                <span className={styles.rowName}>{sp.placeName}</span>
                {opts.meta && <span className={styles.rowMeta}>{opts.meta}</span>}
                {sp.placeAddress && <span className={styles.rowMeta}>{sp.placeAddress}</span>}
                {/* 누가 등록했는지 — 숙소·장소 목록이 모두 이 행을 씁니다 */}
                {sp.createdByNickname && (
                    <span className={styles.rowMeta}>
                        {t('meeting.detailV6.pickedBy', { who: sp.createdByNickname })}
                    </span>
                )}
            </span>
            <span className={styles.rowActions}>
                <button
                    type="button"
                    className={styles.rowAction}
                    onClick={() => { setEditingSubPlace(sp); setSubPlaceFormPrefill(undefined); setShowSubPlaceModal(true); }}
                >
                    {t('meetingEdit.edit')}
                </button>
                <button
                    type="button"
                    className={`${styles.rowAction} ${styles.rowActionDanger}`}
                    onClick={opts.onDelete}
                >
                    {t('meetingEdit.delete')}
                </button>
            </span>
        </li>
    );

    const formatCheckDt = (s?: string) => {
        if (!s) return '';
        const d = new Date(s);
        const dow = DOW_LABELS[d.getDay()];
        return `${d.getMonth() + 1}/${d.getDate()} (${dow}) ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    return (
        <AppScreen title={t('meetingForm.editTitle')} bar="sub" hasFooter>
            {/* 모임 종류는 바꿀 수 없습니다 — 보여만 줍니다 */}
            <Section spacing="tight">
                <div className={styles.tags}>
                    <Badge tone="outline">
                        {isTravelMeeting ? t('meetingForm.travelMeeting') : t('meetingForm.dayMeeting')}
                    </Badge>
                    {isTravelMeeting && travelPeriod && (
                        <Badge tone="weak">
                            {t('meeting.nightsDays', { nights: travelPeriod.nights, days: travelPeriod.days })}
                        </Badge>
                    )}
                </div>
            </Section>

            {/* 모임 정보 */}
            <Section divided>
                <SectionHeader title={t('meetingCreate.meetingInfo')} />
                <FieldGroup>
                    <TextField
                        label={t('meetingCreate.meetingName')}
                        required
                        showCount
                        maxLength={20}
                        placeholder={t('meetingCreate.meetingNamePlaceholder')}
                        value={meetingName}
                        onChange={(e) => setMeetingName(e.target.value.slice(0, 20))}
                    />
                    <TextArea
                        label={t('meetingCreate.description')}
                        showCount
                        maxLength={200}
                        rows={3}
                        placeholder={t('meetingCreate.descriptionPlaceholder')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                    />
                </FieldGroup>

                {/* 정기 모임 — 당일 모임에서만 */}
                {!isTravelMeeting && (
                    <>
                        <SwitchList>
                            <SwitchRow
                                label={t('meetingForm.regularMeeting')}
                                desc={t('meetingForm.regularMeetingDesc')}
                                checked={isRegularMeeting}
                                onChange={(next) => setIsRegularMeeting(next)}
                            />
                        </SwitchList>

                        {isRegularMeeting && (
                            <div className={styles.repeat}>
                                <p className={styles.label}>{t('meetingForm.repeatCycle')}</p>
                                <div className={styles.chips}>
                                    {([
                                        { v: 'DAILY', label: t('meetingForm.daily') },
                                        { v: 'WEEKLY', label: t('meetingForm.weekly') },
                                        { v: 'MONTHLY', label: t('meetingForm.monthly') },
                                    ] as const).map(o => (
                                        <Chip
                                            key={o.v}
                                            label={o.label}
                                            selected={repeatType === o.v}
                                            onSelect={() => { setRepeatType(o.v); setRepeatInterval(1); }}
                                        />
                                    ))}
                                </div>

                                {repeatType === 'WEEKLY' && (
                                    <>
                                        <p className={styles.label}>{t('meetingForm.dayOfWeek')}</p>
                                        <div className={styles.chips}>
                                            {dowList.map(d => (
                                                <Chip
                                                    key={d.v}
                                                    label={d.label}
                                                    selected={repeatDaysOfWeek.includes(d.v)}
                                                    onSelect={() => setRepeatDaysOfWeek(prev =>
                                                        prev.includes(d.v) ? prev.filter(x => x !== d.v) : [...prev, d.v])}
                                                />
                                            ))}
                                        </div>
                                        <p className={styles.label}>{t('meetingForm.interval')}</p>
                                        <div className={styles.chips}>
                                            <Chip
                                                label={t('meetingForm.weekly')}
                                                selected={repeatInterval === 1}
                                                onSelect={() => setRepeatInterval(1)}
                                            />
                                            <Chip
                                                label={t('meetingForm.biweekly')}
                                                selected={repeatInterval === 2}
                                                onSelect={() => setRepeatInterval(2)}
                                            />
                                        </div>
                                    </>
                                )}

                                {repeatType === 'MONTHLY' && (
                                    <>
                                        <p className={styles.label}>{t('meetingForm.dayOfMonth')}</p>
                                        <select
                                            className={styles.select}
                                            value={repeatDayOfMonth}
                                            onChange={(e) => setRepeatDayOfMonth(Number(e.target.value))}
                                            aria-label={t('meetingForm.dayOfMonth')}
                                        >
                                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                                                <option key={d} value={d}>{t('meetingEdit.dayOfMonthN', { n: d })}</option>
                                            ))}
                                        </select>
                                    </>
                                )}

                                <p className={styles.label}>{t('meetingForm.repeatTime')}</p>
                                <input
                                    type="time"
                                    className={styles.select}
                                    value={repeatTime}
                                    onChange={(e) => setRepeatTime(e.target.value)}
                                    aria-label={t('meetingForm.repeatTime')}
                                />
                            </div>
                        )}
                    </>
                )}
            </Section>

            {isTravelMeeting ? (
                <>
                    {/* 여행 정보 */}
                    <Section divided>
                        <SectionHeader title={t('meetingForm.travelInfo')} sub={t('meetingEdit.destHint')} />
                        <ActionList>
                            <ActionRow
                                standalone
                                title={t('meetingForm.travelDest')}
                                value={travelDestination || t('meetingEdit.destSelect')}
                                onClick={() => {
                                    // 여행지 선택 화면으로 가기 전에 지금 폼 상태를 저장합니다.
                                    // TravelDestination 이 결과를 다시 sessionStorage 에 쓰고 returnTo 로 돌아오면
                                    // useEffect 가 newDestination 을 받아 갱신합니다.
                                    try {
                                        sessionStorage.setItem('meeting_create_data', JSON.stringify({
                                            destination: travelDestination,
                                            travelType,
                                            isEditMode: true,
                                            meetingId,
                                            returnTo: `/meeting/edit/${meetingId}`,
                                        }));
                                    } catch { /* ignore */ }
                                    navigate('/meeting/create/travel-destination', {
                                        state: { isEditMode: true, meetingId, returnTo: `/meeting/edit/${meetingId}` },
                                    });
                                }}
                            />
                        </ActionList>

                        <p className={styles.label}>{t('meetingForm.travelType')}</p>
                        <div className={styles.chips}>
                            <Chip
                                label={t('meetingForm.domestic')}
                                selected={travelType === 'DOMESTIC'}
                                onSelect={() => setTravelType('DOMESTIC')}
                            />
                            <Chip
                                label={t('meetingForm.overseas')}
                                selected={travelType === 'OVERSEAS'}
                                onSelect={() => setTravelType('OVERSEAS')}
                            />
                        </div>

                        <p className={styles.label}>{t('meetingForm.travelPeriodLabel')}</p>
                        <div className={styles.range}>
                            <input
                                type="date"
                                className={styles.select}
                                aria-label={t('meetingForm.startDate')}
                                value={travelStartDate ? toDateKey(travelStartDate) : ''}
                                onChange={(e) => setTravelStartDate(e.target.value ? new Date(e.target.value) : null)}
                            />
                            <span className={styles.rangeSep} aria-hidden>~</span>
                            <input
                                type="date"
                                className={styles.select}
                                aria-label={t('meetingForm.endDate')}
                                value={travelEndDate ? toDateKey(travelEndDate) : ''}
                                onChange={(e) => setTravelEndDate(e.target.value ? new Date(e.target.value) : null)}
                            />
                        </div>

                        <p className={styles.label}>{t('meetingForm.gatherTime')}</p>
                        {/* 저장된 값은 "오후 6:00" 인데 input type=time 은 HH:mm 만 받습니다.
                            그대로 넣으면 브라우저가 못 읽어 칸이 빈 채로 보였습니다. 보여줄 때는
                            HH:mm 으로, 저장할 때는 한글로 되돌려 컬럼에 두 형식이 섞이지 않게 합니다. */}
                        <input
                            type="time"
                            className={styles.select}
                            aria-label={t('meetingForm.gatherTime')}
                            value={toHHmm(meetingTime) ?? ''}
                            onChange={(e) => setMeetingTime(toKoreanTime(e.target.value))}
                        />
                    </Section>

                    {/* 집결 장소 */}
                    <Section divided>
                        <SectionHeader title={t('meetingForm.gatherPlace')} />
                        <ActionList>
                            <ActionRow
                                standalone
                                title={location || t('meetingEdit.placeUndecided')}
                                desc={meetingData?.data?.placeSetByNickname
                                    ? t('meeting.detailV6.pickedBy', { who: meetingData.data.placeSetByNickname })
                                    : undefined}
                                onClick={handleLocationChange}
                            />
                        </ActionList>
                    </Section>

                    {/* 숙소 */}
                    <Section divided>
                        <SectionHeader title={t('meetingEdit.hotelTitle')} sub={t('meetingEdit.hotelGuide')} />
                        {hotelSubPlaces.length > 0 && (
                            <ul className={styles.list}>
                                {hotelSubPlaces.map((h, idx) => renderPlaceRow(h, {
                                    tag: t('meetingEdit.hotelNight', { n: idx + 1 }),
                                    meta: (h.startDatetime || h.endDatetime)
                                        ? `${formatCheckDt(h.startDatetime)} ~ ${formatCheckDt(h.endDatetime)}`
                                        : undefined,
                                    onDelete: () => deleteSubPlace(h, fetchHotelSubPlaces),
                                }))}
                            </ul>
                        )}
                        <Button
                            variant="secondary"
                            block
                            onClick={() => {
                                setEditingSubPlace(null);
                                // 기본 체크인/체크아웃 — 첫 숙소는 첫날 15:00 ~ 마지막 날 11:00,
                                // 두 번째부터는 직전 숙소 체크아웃 날짜 15:00 부터
                                let defaultIn = '';
                                let defaultOut = '';
                                if (travelStartDate && travelEndDate) {
                                    const existingCheckOuts = hotelSubPlaces
                                        .map(sp => (sp.endDatetime || '').slice(0, 10))
                                        .filter((d): d is string => !!d)
                                        .sort();
                                    const lastCheckOut = existingCheckOuts.length > 0
                                        ? existingCheckOuts[existingCheckOuts.length - 1]
                                        : null;
                                    defaultIn = lastCheckOut || toDateKey(travelStartDate);
                                    defaultOut = toDateKey(travelEndDate);
                                }
                                setSubPlaceFormPrefill({
                                    placeName: '', placeAddress: '', category: 'HOTEL',
                                    dayNumber: hotelSubPlaces.length + 1, dayNumbers: [hotelSubPlaces.length + 1],
                                    visitTime: '', description: '',
                                    checkInDate: defaultIn, checkInTime: defaultIn ? '15:00' : '',
                                    checkOutDate: defaultOut, checkOutTime: defaultOut ? '11:00' : '',
                                });
                                setShowSubPlaceModal(true);
                            }}
                        >
                            {t('meetingEdit.hotelAdd')}
                        </Button>
                    </Section>
                </>
            ) : (
                <Section divided>
                    <SectionHeader title={t('meetingForm.whenWhere')} />
                    <ActionList>
                        <ActionRow
                            title={t('meetingCreate.dateSelect')}
                            value={date || t('meetingEdit.dateUndecided')}
                            onClick={handleDateChange}
                        />
                        <ActionRow
                            title={t('meetingCreate.timeSelect')}
                            value={time || t('meetingEdit.timeUndecided')}
                            onClick={handleDateChange}
                        />
                        <ActionRow
                            title={t('meetingForm.place')}
                            value={location || t('meetingEdit.placeUndecided')}
                            onClick={handleLocationChange}
                        />
                    </ActionList>
                </Section>
            )}

            {/* 추가 장소 (숙소 제외) — 당일·여행 공통 */}
            <Section divided>
                <SectionHeader
                    title={isTravelMeeting ? t('meetingForm.travelSchedule') : t('meetingForm.extraPlace')}
                />
                {generalSubPlaces.length > 0 && (
                    <ul className={styles.list}>
                        {generalSubPlaces.map(sp => renderPlaceRow(sp, {
                            tag: sp.category || t('meetingEdit.placeGeneric'),
                            meta: sp.visitTime || undefined,
                            onDelete: () => deleteSubPlace(sp, fetchGeneralSubPlaces),
                        }))}
                    </ul>
                )}
                <Button
                    variant="secondary"
                    block
                    onClick={() => {
                        setEditingSubPlace(null);
                        setSubPlaceFormPrefill({
                            placeName: '', placeAddress: '',
                            category: isTravelMeeting ? 'ATTRACTION' : 'SECONDARY',
                            dayNumber: 1, dayNumbers: [1],
                            visitTime: '', description: '',
                        });
                        setShowSubPlaceModal(true);
                    }}
                >
                    {t('meetingForm.addPlace')}
                </Button>
            </Section>

            <Section divided last>
                <p className={styles.warning}>{t('meetingForm.editWarning')}</p>
            </Section>

            <ButtonFooter>
                <Button block disabled={!isSaveEnabled} onClick={handleSave}>
                    {t('common.save')}
                </Button>
            </ButtonFooter>

            {/* 집결지 지도 — 여행 모임 전용. 출발지(집결지) 근처 음식점·카페·관광 추천 */}
            {showGatherMap && (
                <div className={styles.mapLayer}>
                    <AppBar
                        title={t('meetingForm.selectGatherPlace')}
                        mode="close"
                        onClose={() => { setShowGatherMap(false); setSelectedGatherDetail(null); }}
                    />
                    <div className={styles.mapBody}>
                        <PlaceMapView
                            places={gatherPlaces.filter(p => p.lat && p.lng)}
                            center={gatherMapCenter}
                            selectedPlaceId={selectedGatherDetail?.id}
                            showSearch showCategories={false} showBackButton={false}
                            confirmButtonText={t('meetingForm.selectThisLocation')}
                            onPlaceSelect={(p) => setSelectedGatherDetail(p as MapPlace)}
                            onPlaceConfirm={(p) => {
                                const place = p as MapPlace;
                                placePickedRef.current = true;
                                setLocation(place.name);
                                setLocationAddress(place.address || '');
                                setLocationLat(place.lat);
                                setLocationLng(place.lng);
                                setShowGatherMap(false);
                                setSelectedGatherDetail(null);
                            }}
                            onCenterChange={(c) => setGatherAnchorCenter(c)}
                        />
                    </div>
                </div>
            )}

            {/* 숙소·장소 추가/수정 — SubPlaceModal (체크인/아웃 + 시간 겹침 검증) */}
            {showSubPlaceModal && meetingId && (
                <SubPlaceModal
                    isOpen={showSubPlaceModal}
                    onClose={() => {
                        setShowSubPlaceModal(false);
                        setEditingSubPlace(null);
                        setSubPlaceFormPrefill(undefined);
                        if (routeLocation.state?.fromSubPlaceModal) {
                            navigate(routeLocation.pathname, { replace: true, state: {} });
                            setLocationStateProcessed(false);
                        }
                    }}
                    meetingId={meetingId}
                    editingSubPlace={editingSubPlace}
                    isTravelMeeting={isTravelMeeting}
                    travelPeriod={travelStartDate && travelEndDate ? {
                        days: Math.ceil((travelEndDate.getTime() - travelStartDate.getTime()) / 86400000) + 1,
                        nights: Math.ceil((travelEndDate.getTime() - travelStartDate.getTime()) / 86400000),
                        start: travelStartDate, end: travelEndDate,
                    } : null}
                    meetingTime={meetingTime}
                    subPlaces={[...hotelSubPlaces, ...generalSubPlaces]}
                    onSaveSuccess={() => {
                        // 호텔·일반 모두 갱신 — 어떤 카테고리로 저장됐든 한 번에 반영
                        fetchHotelSubPlaces();
                        fetchGeneralSubPlaces();
                        setShowSubPlaceModal(false);
                        setEditingSubPlace(null);
                        setSubPlaceFormPrefill(undefined);
                        if (routeLocation.state?.fromSubPlaceModal) {
                            navigate(routeLocation.pathname, { replace: true, state: {} });
                            setLocationStateProcessed(false);
                        }
                    }}
                    returnTo={`/meeting/edit/${meetingId}`}
                    initialForm={subPlaceFormPrefill}
                    travelDestinationName={travelDestination}
                    travelDestinationCenter={destCenter || null}
                    meetingScheduleStartDatetime={meetingData?.data?.schedule?.startDatetime}
                    meetingScheduleEndDatetime={meetingData?.data?.schedule?.endDatetime}
                    meetingScheduleId={meetingData?.data?.confirmedScheduleId || meetingData?.data?.schedule?.scheduleId}
                    mainPlaceName={location}
                />
            )}
        </AppScreen>
    );
};
